import {
  conciliationReportsAxios,
  conciliationInitAxios,
  conciliationDashboardAxios,
  thirdPartyAxios,
} from '../config/axiosInstances';
import {
  Permission,
  FindConciliationsStatusResponse,
  ConciliationHeaderId,
  ConciliationDetailErrors,
  ConciliationHeaderState,
  FindClosureEvolutionResponse,
} from '../types';

export type GetPermissionsResponse = Permission[];

export type GetActiveCountriesResponse = {
  division_id: string;
  division: string;
  country: string;
  abr_3: string;
  active: boolean;
  country_id: string;
  name: string;
}[];

export type GetActiveStoresResponse = {
  oak_store_id: string;
  country_description: string;
  store_id: string;
  cost_center: string;
  created_date: string;
  br_base_store_id: string;
  close_date: string;
  br_franchise_ind: string;
  country: string;
  store_more_modify_date: string;
  name: string;
  group_id: string;
  estado_sgl: string;
  open_date: string;
  postal_code: string;
  city: string;
  state_province: string;
  cidade_id: string;
  address_line_1: string;
  store_modify_date: string;
  mail_user: string;
  country_id: string;
  phone: string;
  storeAcronym: string;
}[];

export type GetOpStructuresResponse = {
  division_id: string;
  division: string;
  country_id: string;
  country: string;
  regional_mgmt_id: string | null;
  regional_mgmt: string | null;
  region_id: string | null;
  region: string | null;
  mgmt_id: string | null;
  mgmt: string | null;
  supervision_id: string | null;
  supervision: string | null;
  store_entity_id: string;
  store_acronym: string;
  company_id: string | null;
  company_name: string | null;
  agreement_id: string | null;
  agreement_type: string | null;
}[];

export interface FindConciliationDetailResponse {
  conciliation_header_id?: number | string;
  country: string;
  store: string;
  accounting_date: string;
  fiscal_id: number | string;
  fiscal_key: number | string;
  pos_num: number | string;
  error: string;
  transaction_type_fv: number | string;
  discount_on_total_amt_fv: number | string;
  transaction_amt_fv: number | string;
  transaction_type_fs: number | string;
  discount_on_total_amt_fs: number | string;
  transaction_amt_fs: number | string;
  xjust: string;
  business_date_fv: number | string;
  qty_sales_fv: number | string;
  qty_sales_fs: number | string;
  GCs: number | string;
}

export interface FindConciliationDetailRequest {
  countryAcronym: string | null | undefined;
  storeAcronym?: string | undefined;
  accountingDateRange?: string;
  errors?: string | undefined;
  pos?: string;
  sentToOnedrive: boolean;
  conciliationHeaderId?: ConciliationHeaderId;
  regionalMgmtId?: string;
  regionId?: string;
  mgmtId?: string;
  supervisionId?: string;
  companyId?: string;
  agreementId?: string;
}

export interface FindConciliationHeaderResponse {
  conciliation_header_id: string;
  country: string;
  store: string;
  accounting_date: string;
  state: string;
  gcs: number;
  total_amt: number;
  gcs_fs: number;
  total_amt_fs: number;
  gcs_ok: number;
  total_amt_ok: number;
  gcs_dif: number;
  total_amt_dif: number;
  gcs_fv: number;
  total_amt_fv: number;
  gcs_fiscal: number;
  total_amt_fiscal: number;
  porc_ok: number;
  qty_canc_sust: number;
  total_amt_canc_sust: number;
}

export interface FindConciliationHeaderRequest {
  countryAcronym: string | null | undefined;
  storeAcronym?: string | undefined;
  accountingDateRange?: string;
  status?: string | undefined;
  franchiseTypes?: string;
  federativeEntities?: string;
  sentToOnedrive: boolean;
  regionalMgmtId?: string;
  regionId?: string;
  mgmtId?: string;
  supervisionId?: string;
  companyId?: string;
  agreementId?: string;
}

export interface FindConciliationsStatusRequest {
  countryAcronym: string;
  accountingDateRange: string;
}

export interface FindSovosImportRequest {
  countryAcronym: string;
}

export type RuleDefinitionResponse = {
  id: number;
  code: string;
  description: string;
};

export type CountriesRulesResponse = {
  definitions: RuleDefinitionResponse[];
  assignments: { [countryId: string]: number[] };
  conciliationMethods: { [countryId: string]: 'G' | 'D' };
};

export type ReportLimits = {
  headerLimitDays: number;
  detailLimitDays: number;
  synchroLimitDays: number;
};

export type GetParametersResponse = {
  filtersLimitMonths?: number | string;
  dashboardTimer?: number | string /** minutes */;
  statuses: ConciliationHeaderState[];
  errors: ConciliationDetailErrors[];
  countries_rules?: CountriesRulesResponse;
  reportLimits: ReportLimits | null;
};

export type FindSovosStatusResponse = {
  import_date: string;
  country: string;
  status: string;
  total_tickets: number;
  amount: number;
}[];

export type FindMastersStatusResponse = {
  import_date: string;
  status: string;
}[];

export interface FindMastersImportRequest {
  countries?: string;
  importDateRange?: string;
  state?: string;
}

export type FindMastersImportResponse = {
  import_date: string;
  type: string;
  id: string;
  description: string;
  quantity: number;
  state: string;
}[];

export interface MastersImportRequest {
  master_type?: string;
  countries?: string;
  days?: number;
  only_active?: boolean;
}

export type FindMastersTypesResponse = string[];

export interface FindFiscalDetailRequest {
  countries?: string;
  stores?: string;
  authorizationDateRange?: string;
}

export type FindFiscalDetailResponse = {
  authorization_date: string;
  country: string;
  store: string;
  fiscal_id: string | number;
  model: string;
  status: string;
  registry_qty: number;
}[];
export interface FindDashboardTotalsByCountryRequest {
  countryAcronym: string;
  accountingDateRange: string;
}

export type FindDashboardTotalsByCountryResponse = {
  countryAcronym: string;
  flex_amount: number;
  flex_gcs: number;
  sovos_amount: number;
  sovos_gcs: number;
}[];

export type FindClosureEvolutionRequest = {
  countryAcronym: string; // "BR,PA"
  storeAcronym?: string; // "SP01,SP02" (opcional)
  accountingDateRange: string; // dateRangeRequestMap(...)
};

export type GetPosResponse = number[];

export default class {
  static async getStores() {
    return conciliationInitAxios.get<GetPermissionsResponse>('/stores');
  }

  static async getParameters() {
    return conciliationInitAxios.get<GetParametersResponse>('/parameters');
  }

  static async getPermissions() {
    return conciliationInitAxios.get<GetPermissionsResponse>('/permissions');
  }

  static async getActiveCountries() {
    return conciliationInitAxios.get<GetActiveCountriesResponse>('/active-countries');
  }

  static getPos(params: {
    countryAcronym: string;
    storeAcronym: string;
    accountingDateRange: string; // "YYYY-MM-DD,YYYY-MM-DD"
  }) {
    return conciliationInitAxios.get<GetPosResponse>('/pos', { params });
  }

  static async findConciliationDetail(request: FindConciliationDetailRequest) {
    return conciliationReportsAxios.get<FindConciliationDetailResponse>(
      `/conciliations/error-details`,
      {
        params: request,
      }
    );
  }

  static async findConciliationHeader(request: FindConciliationHeaderRequest) {
    return conciliationReportsAxios.get<FindConciliationHeaderResponse>(
      `/conciliations/total-details`,
      {
        params: request,
      }
    );
  }

  static async findDashboardConciliationsState(request: FindConciliationsStatusRequest) {
    return conciliationDashboardAxios.get<FindConciliationsStatusResponse>(
      `/dashboard/conciliations`,
      {
        params: request,
      }
    );
  }

  static async findSovosStatus(request: FindSovosImportRequest) {
    return conciliationDashboardAxios.get<FindSovosStatusResponse>(
      '/dashboard/sovos-status-import',
      { params: request }
    );
  }

  static async findSovosDetail(request: FindFiscalDetailRequest) {
    return conciliationReportsAxios.get<FindFiscalDetailResponse>(`/sovos-status-import-details`, {
      params: request,
    });
  }

  static async findMastersStatus2() {
    return conciliationDashboardAxios.get<FindMastersStatusResponse>(
      '/dashboard/masters-status-new'
    );
  }

  static async findMastersImport(request: FindMastersImportRequest) {
    return conciliationReportsAxios.get<FindMastersImportResponse>(`/masters_status_details_new`, {
      params: request,
    });
  }

  static async findMastersTypes() {
    return conciliationReportsAxios.get<FindMastersTypesResponse>('/masters-types');
  }

  static async mastersImportExecution(data: MastersImportRequest) {
    return thirdPartyAxios.post(`/masters-import`, data);
  }

  static async findDashboardTotalsByCountry(request: FindDashboardTotalsByCountryRequest) {
    return conciliationDashboardAxios.get<FindDashboardTotalsByCountryResponse>(
      '/dashboard/totals-by-country',
      { params: request }
    );
  }

  static async findDashboardClosureEvolution(request: FindClosureEvolutionRequest) {
    return conciliationDashboardAxios.get<FindClosureEvolutionResponse>(
      '/dashboard/closure-evolution',
      { params: request }
    );
  }

  static async getOpStructures() {
    return conciliationInitAxios.get<GetOpStructuresResponse>('/op-structures');
  }
}
